import { fromJS } from "immutable";
import {
  CARE_WATCH_STATUSES,
  CARE_WATCH_VIEWS,
  DEFAULT_PAGE_SIZE,
  getDefaultFilters,
  isCareWatchLiveView,
  isCareWatchSignalView,
} from "pages/CareWatch/utils/constants";
import CARE_WATCH_ACTIONS from "../actions/careWatch.actions";

/** Default filter values for a single tab (everything except `view`). */
const tabFilterDefaults = () => {
  const all = getDefaultFilters();
  const { view: _v, ...rest } = all;
  return rest;
};

const applyFilterKeys = (state, values) => {
  let next = state;
  Object.keys(values).forEach((key) => {
    next = next.setIn(["filters", key], values[key]);
  });
  return next;
};

/**
 * Incident concerns and Missed documentation share the date range.
 * Every other filter goes back to its default on that switch.
 * Live keeps a separate snapshot and does not read or write that range.
 */
const applyViewChange = (previous, next, newView) => {
  const oldView = previous.getIn(["filters", "view"]);
  const oldFilters = previous.get("filters").toJS();

  if (isCareWatchSignalView(oldView) && isCareWatchSignalView(newView)) {
    const defaults = tabFilterDefaults();
    Object.keys(defaults).forEach((key) => {
      if (key === "fromDate" || key === "toDate") return;
      next = next.setIn(["filters", key], defaults[key]);
    });
    return next.set("activeKpi", null);
  }

  if (isCareWatchLiveView(newView)) {
    next = next.set(
      "signalDates",
      fromJS({
        fromDate: oldFilters.fromDate,
        toDate: oldFilters.toDate,
      })
    );
    const saved = previous.getIn(["tabFilters", CARE_WATCH_VIEWS.LIVE]);
    const restored =
      saved && saved.size > 0 ? saved.toJS() : tabFilterDefaults();
    const { fromDate: _from, toDate: _to, ...liveOnly } = restored;
    const defaults = tabFilterDefaults();
    next = applyFilterKeys(next, {
      ...defaults,
      ...liveOnly,
      fromDate: defaults.fromDate,
      toDate: defaults.toDate,
    });
    return next.set("activeKpi", null);
  }

  if (isCareWatchLiveView(oldView) && isCareWatchSignalView(newView)) {
    const {
      view: _view,
      fromDate: _from,
      toDate: _to,
      ...liveState
    } = oldFilters;
    next = next.setIn(
      ["tabFilters", CARE_WATCH_VIEWS.LIVE],
      fromJS(liveState)
    );
    const shared = previous.get("signalDates");
    const dates = shared
      ? {
          fromDate: shared.get("fromDate") ?? null,
          toDate: shared.get("toDate") ?? null,
        }
      : {};
    next = applyFilterKeys(next, { ...tabFilterDefaults(), ...dates });
    return next.set("activeKpi", null);
  }

  return next.set("activeKpi", null);
};

const buildInitialState = () =>
  fromJS({
    items: [],
    total: 0,
    serverPaged: false,
    listLoading: false,
    listError: "",

    // Server-computed KPI counts. Kept separate from `items` because the tiles
    // count every row in the current tab/status, not one paginated page.
    overview: {},
    overviewLoading: false,

    filters: getDefaultFilters(),
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    selectedId: null,
    checkedIds: [],
    activeKpi: null,
    promoteHighlight: false,

    // Date range shared by Incident concerns and Missed documentation.
    // Live keeps its own dates in tabFilters and is not part of this.
    signalDates: {
      fromDate: getDefaultFilters().fromDate,
      toDate: getDefaultFilters().toDate,
    },
    // Live's own filters. The two signal tabs do not snapshot here.
    tabFilters: {
      [CARE_WATCH_VIEWS.LIVE]: null,
    },

    // On-demand detail: full signal/live data loaded when a row is selected.
    selectedDetail: null,
    detailLoading: false,

    acknowledgeLoading: false,
    promoteLoading: false,
    riskSavingId: null,
    updatedByPusher: null,
  });

const initialState = buildInitialState();

/** Web rows are `cw-1875`. Acknowledge answers with `1875`. */
const careWatchKey = (id) => String(id ?? "").replace(/^cw-/i, "");

const matchesCareWatchId = (ids, id) => {
  const key = careWatchKey(id);
  return (ids || []).some((candidate) => careWatchKey(candidate) === key);
};

const updateItemsByIds = (state, ids, updater) => {
  const items = state.get("items").toJS();
  return state.set(
    "items",
    fromJS(
      items.map((item) => (matchesCareWatchId(ids, item.id) ? updater(item) : item))
    )
  );
};

export default function careWatchReducer(state = initialState, action) {
  switch (action.type) {
    case CARE_WATCH_ACTIONS.LIST.PENDING:
      return state.set("listLoading", true).set("listError", "");
    case CARE_WATCH_ACTIONS.LIST.SUCCESS: {
      const payload = action.data;
      const serverPaged = Boolean(payload && !Array.isArray(payload) && payload.serverPaged);
      const items = serverPaged ? payload.items || [] : payload || [];
      let next = state
        .set("listLoading", false)
        .set("listError", "")
        .set("serverPaged", serverPaged)
        .set("items", fromJS(items))
        .set("total", serverPaged ? Number(payload.total || 0) : items.length);
      if (serverPaged && payload.page) {
        next = next.set("page", Number(payload.page));
      }
      return next;
    }
    case CARE_WATCH_ACTIONS.LIST.ERROR:
      return state.set("listLoading", false).set("listError", action.error || "");

    case CARE_WATCH_ACTIONS.DETAIL.PENDING:
      return state.set("detailLoading", true);
    case CARE_WATCH_ACTIONS.DETAIL.SUCCESS:
      return state
        .set("detailLoading", false)
        .set("selectedDetail", fromJS(action.data));
    case CARE_WATCH_ACTIONS.DETAIL.ERROR:
      return state.set("detailLoading", false);

    case CARE_WATCH_ACTIONS.OVERVIEW.PENDING:
      return state.set("overviewLoading", true);
    case CARE_WATCH_ACTIONS.OVERVIEW.SUCCESS:
      return state
        .set("overviewLoading", false)
        .set("overview", fromJS(action.data || {}));
    case CARE_WATCH_ACTIONS.OVERVIEW.ERROR:
      // Leave the previous counts on screen; a failed refresh should not blank
      // the tiles while the table still shows rows.
      return state.set("overviewLoading", false);

    case CARE_WATCH_ACTIONS.ACKNOWLEDGE.PENDING:
      return state.set("acknowledgeLoading", true);
    case CARE_WATCH_ACTIONS.ACKNOWLEDGE.SUCCESS: {
      const ids = action.data?.ids || [];
      return updateItemsByIds(state, ids, (item) => ({
        ...item,
        status: CARE_WATCH_STATUSES.ACKNOWLEDGED,
        overdue: false,
      }))
        .set("acknowledgeLoading", false)
        .set(
          "checkedIds",
          fromJS(
            state
              .get("checkedIds")
              .toJS()
              .filter((id) => !matchesCareWatchId(ids, id))
          )
        )
        .set(
          "selectedId",
          matchesCareWatchId(ids, state.get("selectedId"))
            ? null
            : state.get("selectedId")
        )
        .set("promoteHighlight", false);
    }
    case CARE_WATCH_ACTIONS.ACKNOWLEDGE.ERROR:
      return state.set("acknowledgeLoading", false);

    case CARE_WATCH_ACTIONS.SET_RISK.PENDING:
      return state.set("riskSavingId", action.id ?? null);
    case CARE_WATCH_ACTIONS.SET_RISK.SUCCESS: {
      const id = action.data?.id;
      const risk = action.data?.risk;
      const items = state.get("items").toJS().map((item) =>
        id && item.id === id ? { ...item, risk } : item
      );
      let next = state.set("items", fromJS(items)).set("riskSavingId", null);
      const detail = next.get("selectedDetail");
      if (detail && id && detail.get("id") === id) {
        next = next.set("selectedDetail", detail.set("risk", risk));
      }
      return next;
    }
    case CARE_WATCH_ACTIONS.SET_RISK.ERROR:
      return state.set("riskSavingId", null);

    case CARE_WATCH_ACTIONS.PROMOTE.PENDING:
      return state.set("promoteLoading", true);
    case CARE_WATCH_ACTIONS.PROMOTE.SUCCESS: {
      const id = action.data?.id;
      return updateItemsByIds(state, [id], (item) => ({
        ...item,
        status: CARE_WATCH_STATUSES.PROMOTED,
        promoted: true,
        overdue: false,
      }))
        .set("promoteLoading", false)
        .set("promoteHighlight", true);
    }
    case CARE_WATCH_ACTIONS.PROMOTE.ERROR:
      return state.set("promoteLoading", false);

    case CARE_WATCH_ACTIONS.SET_FILTER: {
      const { key, value } = action.payload;
      let next = state
        .setIn(["filters", key], value)
        .set("page", 1)
        .set("selectedId", null)
        .set("checkedIds", fromJS([]))
        .set("promoteHighlight", false);
      if (key === "view") {
        next = applyViewChange(state, next, value);
      } else if (
        (key === "fromDate" || key === "toDate") &&
        isCareWatchSignalView(state.getIn(["filters", "view"]))
      ) {
        next = next.setIn(["signalDates", key], value);
      }
      return next;
    }

    case CARE_WATCH_ACTIONS.SET_FILTERS: {
      const updates = action.payload || {};
      let next = state
        .set("page", 1)
        .set("selectedId", null)
        .set("checkedIds", fromJS([]))
        .set("promoteHighlight", false);
      Object.keys(updates).forEach((key) => {
        next = next.setIn(["filters", key], updates[key]);
      });
      if (isCareWatchSignalView(state.getIn(["filters", "view"]))) {
        if (Object.prototype.hasOwnProperty.call(updates, "fromDate")) {
          next = next.setIn(["signalDates", "fromDate"], updates.fromDate ?? null);
        }
        if (Object.prototype.hasOwnProperty.call(updates, "toDate")) {
          next = next.setIn(["signalDates", "toDate"], updates.toDate ?? null);
        }
      }
      return next;
    }

    case CARE_WATCH_ACTIONS.CLEAR_FILTERS: {
      const payload = action.payload || {};
      const currentView = state.getIn(["filters", "view"]);
      const view = currentView || getDefaultFilters().view;
      const defaults = getDefaultFilters();
      const dates =
        payload.fromDate && payload.toDate
          ? { fromDate: payload.fromDate, toDate: payload.toDate }
          : { fromDate: defaults.fromDate, toDate: defaults.toDate };
      let next = state
        .set(
          "filters",
          fromJS({
            ...defaults,
            view,
            ...dates,
          })
        )
        .set("activeKpi", null)
        .set("page", 1)
        .set("selectedId", null)
        .set("checkedIds", fromJS([]))
        .set("promoteHighlight", false);
      if (isCareWatchSignalView(view)) {
        next = next.set("signalDates", fromJS(dates));
      }
      if (isCareWatchLiveView(view)) {
        next = next.setIn(["tabFilters", CARE_WATCH_VIEWS.LIVE], null);
      }
      return next;
    }

    case CARE_WATCH_ACTIONS.SET_PAGE:
      return state.set("page", action.payload).set("promoteHighlight", false);

    case CARE_WATCH_ACTIONS.SET_PAGE_SIZE:
      return state
        .set("pageSize", action.payload)
        .set("page", 1)
        .set("promoteHighlight", false);

    case CARE_WATCH_ACTIONS.TOGGLE_ROW:
    case CARE_WATCH_ACTIONS.TOGGLE_CHECKED: {
      const next = state.get("selectedId") === action.payload ? null : action.payload;
      return state
        .set("selectedId", next)
        .set("checkedIds", fromJS(next ? [next] : []))
        .set("promoteHighlight", false)
        .set("selectedDetail", null)
        .set("detailLoading", false);
    }

    case CARE_WATCH_ACTIONS.SET_SELECTED:
      return state
        .set("selectedId", action.payload)
        .set("checkedIds", fromJS(action.payload ? [action.payload] : []))
        .set("promoteHighlight", false)
        .set("selectedDetail", null)
        .set("detailLoading", false);

    case CARE_WATCH_ACTIONS.TOGGLE_ALL_PAGE: {
      const pageIds = action.payload || [];
      const current = state.get("checkedIds").toJS();
      const allChecked =
        pageIds.length > 0 && pageIds.every((id) => current.includes(id));
      const next = allChecked
        ? current.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...current, ...pageIds]));
      return state.set("checkedIds", fromJS(next));
    }

    case CARE_WATCH_ACTIONS.CLEAR_CHECKED:
      return state
        .set("checkedIds", fromJS([]))
        .set("selectedId", null);

    case CARE_WATCH_ACTIONS.SET_ACTIVE_KPI:
      return state
        .set("activeKpi", action.payload)
        .set("page", 1)
        .set("selectedId", null)
        .set("checkedIds", fromJS([]));

    case CARE_WATCH_ACTIONS.UPDATED_PUSHER_RECEIVED:
      return state.set("updatedByPusher", action.data || {});

    case CARE_WATCH_ACTIONS.RESET_UPDATED_PUSHER:
      return state.set("updatedByPusher", null);

    case CARE_WATCH_ACTIONS.RESET:
      return buildInitialState();

    default:
      return state;
  }
}
